import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { RouterService } from '@cortex-shim/router.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { signal } from '@preact/signals-react';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import { RedRobinScreen, ViewRedRobinAnalyticActions, ViewRedRobinController } from '../view_red_robin.controller';
import { RedRobinApiService } from '@api_patient/red_robin_api.service';
import dayjs from 'dayjs';

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenWhoIsRespondantController implements ControllerProtocol {
	public viewData = signal<PresentationModel>({ ninPlaceholder: '', countryNinHasAgeSupport: true, useNhsNumber: false });

	constructor(
		private readonly viewRedRobinController: ViewRedRobinController,
		private readonly redRobinApiService: RedRobinApiService,
		private readonly routerService: RouterService,
		private readonly analyticService: AnalyticService,
	) {}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'RedRobin: Who is respondant',
		});

		// If the licenceholder does not allow representative, skip this screen
		if (!this.viewRedRobinController.flowData.peek().value?.allowRepresentative) {
			this.analyticService.trackEvent(AnalyticCategories.Booking, ViewRedRobinAnalyticActions.DoesNotAllowRepresentative);

			this.viewRedRobinController.createSesssion(true, null);

			return;
		}

		this.viewData.value = {
			ninPlaceholder: NiNFormatter.formatWithPlaceholder(this.viewRedRobinController.getFlowData().currentCountry, '')[1] ?? '',
			countryNinHasAgeSupport: ['SE', 'NO', 'FI'].includes(this.viewRedRobinController.getFlowData().currentCountry),
			useNhsNumber: this.viewRedRobinController.getFlowData().currentCountry === 'GB',
		};
	}

	public async submit(data: FormData) {
		if (data.respondentType === 'me') {
			this.viewRedRobinController.createSesssion(true, null);

			return;
		}

		const ninCountryInstance = NiN.produce(this.viewRedRobinController.getFlowData().currentCountry);
		const ninNormalized = ninCountryInstance.normalize(data.patientNin ?? '');
		let dateOfBirth = ninCountryInstance.getDateOfBirth(ninNormalized);
		if (!this.viewData.value.countryNinHasAgeSupport && data.dateOfBirth) {
			dateOfBirth = new Date(data.dateOfBirth);
		}

		const age = dayjs().diff(dayjs(dateOfBirth), 'years');
		if (age >= 1) {
			this.viewRedRobinController.createSesssion(false, data.patientNin);
		} else {
			this.analyticService.trackEvent(
				AnalyticCategories.RedRobin,
				ViewRedRobinAnalyticActions.PatientUnderOneYearOfAge,
				'Patient is under 1 year of age',
			);

			let queryAddition = '';
			if (data.respondentType === 'representative' && data.patientNin) {
				queryAddition = `?nin=${data.patientNin}`;
			}
			this.routerService.navigateByUrl(`../message${queryAddition}`, { replace: true });

			// TODO: Remove when angular is gone!
			if (
				(window as typeof window & { __TEMP__overrideNavigation: Record<string, unknown> | undefined })[
					'__TEMP__overrideNavigation'
				] != null
			) {
				window.location.reload();
			}
		}
	}
}

export type RespondentType = 'me' | 'representative';

interface PresentationModel {
	ninPlaceholder: string;
	countryNinHasAgeSupport: boolean;
	useNhsNumber: boolean;
}

interface FormData {
	respondentType: RespondentType;
	patientNin: Nullable<string>;
	dateOfBirth: Nullable<string>;
}
